import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ShoppingCartSimple } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Inventory", href: "/inventory", type: "route" },
  { label: "Services", href: "/#services", type: "anchor" },
  { label: "Sales", href: "/#sales", type: "anchor" },
  { label: "Safety", href: "/#safety", type: "anchor" },
  { label: "Contact", href: "/#contact", type: "anchor" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const id = href.split("#")[1];
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.querySelector(`#${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-white/10"
          : "bg-background/40 backdrop-blur-md border-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="h-2.5 w-2.5 bg-primary block group-hover:rotate-45 transition-transform duration-300" />
          <span className="font-display font-black tracking-tighter text-sm md:text-base uppercase leading-none">
            B&amp;C System<span className="text-primary">.</span>
            <span className="block mono-label text-[0.55rem] text-muted-foreground tracking-[0.25em] mt-0.5">
              Electrical &amp; Wireline
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.href}
                to={l.href}
                className="mono-label text-muted-foreground hover:text-foreground transition-colors link-underline"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => goAnchor(e, l.href)}
                className="mono-label text-muted-foreground hover:text-foreground transition-colors link-underline"
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </a>
            )
          )}
          <Link
            to="/cart"
            className="relative mono-label border border-white/15 px-4 py-2.5 hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
            data-testid="nav-cart"
          >
            <ShoppingCartSimple size={16} weight="bold" />
            Cart
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[0.6rem] min-w-[18px] h-[18px] px-1 flex items-center justify-center font-mono-tech"
                data-testid="nav-cart-count"
              >
                {count}
              </span>
            )}
          </Link>
          <Link
            to="/inventory"
            className="mono-label bg-primary text-primary-foreground px-5 py-3 hover:bg-foreground hover:text-background transition-colors duration-200"
            data-testid="nav-cta"
          >
            Shop Now
          </Link>
        </nav>

        <div className="md:hidden flex items-center gap-3">
          <Link
            to="/cart"
            className="relative p-2"
            aria-label="Cart"
            data-testid="nav-cart-mobile"
          >
            <ShoppingCartSimple size={22} weight="light" />
            {count > 0 && (
              <span className="absolute -top-0 -right-0 bg-primary text-primary-foreground text-[0.6rem] min-w-[16px] h-[16px] px-1 flex items-center justify-center font-mono-tech">
                {count}
              </span>
            )}
          </Link>
          <button
            className="text-foreground p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} weight="light" /> : <List size={24} weight="light" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-background border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) =>
                l.type === "route" ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="mono-label text-foreground"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={(e) => goAnchor(e, l.href)}
                    className="mono-label text-foreground"
                  >
                    {l.label}
                  </a>
                )
              )}
              <Link
                to="/inventory"
                onClick={() => setOpen(false)}
                className="mono-label bg-primary text-primary-foreground px-5 py-3 text-center"
              >
                Shop Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
